
import { useEffect, useRef, useState } from "react";
import {
    Box,
    Grid,
    Button,
    Card,
    CardContent,
    Tooltip,
    IconButton,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
} from "@mui/material";
import { Edit, Delete, ContentCopy } from "@mui/icons-material";
import axios from "axios";
import CustomTable from "../../components/customTable/CustomTable";
import { createMRTColumnHelper } from "material-react-table";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { faqApi } from "../../app/slices/faqSlice";
import JoditEditor from "jodit-react";
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from "../../utils/axiosInstance";


const Faq = () => {
    const baseUrl = import.meta.env.VITE_BASE_URL;
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(faqApi());
    }, [dispatch]);

    const sliceResponse = useSelector((state) => state.faq.faq.data);
    const stateLoading = useSelector((state) => state.faq.loading);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [formData, setFormData] = useState({
        _id: null,
        question: "",
        answer: "",
        user_type: ""
    });
    const editor = useRef(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const { question, answer, user_type } = formData;

        if (!user_type) {
            Swal.fire({
                text: "User Type required.",
                icon: "warning",
                timer: 3000,
                showConfirmButton: false,
            });
            return false;
        }
        if (!question.trim() || !answer.trim()) {
            Swal.fire({
                text: "Both Question and Answer are required.",
                icon: "warning",
                timer: 3000,
                showConfirmButton: false,
            });
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setSubmitLoading(true);
        const isUpdate = !!formData._id;
        const url = isUpdate
            ? `/faq/update/${formData._id}`
            : `/faq/create`;
        // const url = isUpdate
        //     ? `${baseUrl}/faq/update/${formData._id}`
        //     : `${baseUrl}/faq/create`;
        const method = isUpdate ? "put" : "post";

        try {
            const response = await axiosInstance [method](url, formData);
            // const response = await axios[method](url, formData, { headers });

            if (response.data.error === false) {
                Swal.fire({
                    text: response.data?.message || "FAQ saved successfully",
                    icon: "success",
                    timer: 3000,
                    showConfirmButton: false,
                });
                setFormData({ _id: null, question: "", answer: "", user_type: "" });
                dispatch(faqApi());
            } else {
                Swal.fire({
                    text: response.data?.message || "Something went wrong!",
                    icon: "error",
                    timer: 3000,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            console.error("API Error:", error);
            Swal.fire({
                text: "Something went wrong",
                icon: "error",
                timer: 3000,
                showConfirmButton: false,
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleEdit = (row) => {
        setFormData({
            _id: row._id,
            question: row.question,
            answer: row.answer,
            user_type: row.user_type
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await axiosInstance.delete(`/faq/destroy/${id}`);
                    // const response = await axios.delete(`${baseUrl}/faq/destroy/${id}`, { headers });
                    if (response.data.error === false) {
                        Swal.fire({
                            text: response.data?.message || "Deleted successfully",
                            icon: "success",
                            timer: 3000,
                            showConfirmButton: false,
                        });
                        dispatch(faqApi());
                        setFormData({ _id: null, question: "", answer: "", user_type: "" });

                    } else {
                        Swal.fire({
                            text: response.data?.message || "Something went wrong!",
                            icon: "error",
                            timer: 3000,
                            showConfirmButton: false,
                        });
                    }
                } catch (error) {
                    Swal.fire({
                        text: "Server error while deleting",
                        icon: "error",
                        timer: 3000,
                        showConfirmButton: false,
                    });
                }
            }
        });
    };



    const [open, setOpen] = useState(false);
    const [fullContent, setfullContent] = useState(null);

    const columnHelper = createMRTColumnHelper();

    const columns = [

        columnHelper.accessor("_id", {
            header: "ID",
            size: 30,
            Cell: ({ cell }) => {
                const handleCopy = () => {
                    navigator.clipboard.writeText(cell.row.original?._id);
                    toast.success("Copied to clipboard!");
                };
                return (
                    <Tooltip title={cell.row.original?._id}>
                        <IconButton onClick={handleCopy}>
                            <ContentCopy />
                        </IconButton>
                    </Tooltip>
                );
            },
        }),
        columnHelper.accessor("user_type", {
            header: "User Type",
        }),
        columnHelper.accessor("question", {
            header: "Question",
        }),
        // columnHelper.accessor("answer", {
        //     header: "Answer",
        // }),

        columnHelper.accessor("answer", {
            header: "Answer",
            Cell: ({ cell }) => (
                <Button onClick={() => { setOpen(true); setfullContent(cell.row.original?.answer || "") }} variant="contained" > View Content</Button>
            ),
        }),

        columnHelper.accessor("createdAt", {
            header: "Created At",
            Cell: (cell) => cell.row.original.createdAt?.split("T")[0],
        }),
        columnHelper.accessor("updatedAt", {
            header: "Updated At",
            Cell: (cell) => cell.row.original.updatedAt?.split("T")[0],
        }),

        columnHelper.display({
            id: "actions",
            header: "Actions",
            Cell: ({ row }) => (
                <>
                    <Tooltip title="Edit">
                        <IconButton onClick={() => handleEdit(row.original)} color="primary">
                            <Edit />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton onClick={() => handleDelete(row.original._id)} color="error">
                            <Delete />
                        </IconButton>
                    </Tooltip>
                </>
            ),
        }),

    ];


    return (
        <>
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Box component="form" onSubmit={(e) => e.preventDefault()}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        fullWidth
                                        label="User Type"
                                        name="user_type"
                                        value={formData.user_type}
                                        onChange={handleChange}
                                        required
                                    >
                                        <MenuItem value="partner">Partner</MenuItem>
                                        <MenuItem value="website">Website</MenuItem>
                                        <MenuItem value="both">both</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Question"
                                    name="question"
                                    value={formData.question}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>
                                    Answer <span style={{ color: 'red' }}>*</span>
                                </label>
                                <JoditEditor
                                    ref={editor}
                                    value={formData.answer}
                                    tabIndex={1}
                                    onChange={(newContent) =>
                                        setFormData((prev) => ({ ...prev, answer: newContent }))
                                    }
                                />
                            </Grid>

                            {/* <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Answer"
                                    name="answer"
                                    value={formData.answer}
                                    onChange={handleChange}
                                    multiline
                                    rows={4}
                                    required
                                />
                            </Grid> */}
                            <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={submitLoading}
                                    onClick={handleSubmit}
                                    sx={{
                                        float: "right",
                                        p: { xs: 1, sm: 1.8 },
                                        transition: "transform 0.5s ease-in-out",
                                        "&:hover": {
                                            transform: "scale(1.01)",
                                        },
                                    }}
                                >
                                    {submitLoading
                                        ? "Submitting..."
                                        : formData._id
                                            ? "Update"
                                            : "Create"}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            </Card>

            <CustomTable
                columns={columns}
                data={sliceResponse}
                loading={stateLoading}
                permissionKey="Faq"
            />

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Full Content
                    <Tooltip title="Close">
                        <IconButton sx={{ float: "right" }} onClick={() => { setOpen(false) }} aria-label="delete" size="small">
                            <CloseIcon fontSize="inherit" color="error" sx={{ color: "red" }} />
                        </IconButton>
                    </Tooltip>
                </DialogTitle>
                <DialogContent dividers>
                    <div dangerouslySetInnerHTML={{ __html: fullContent }} />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Faq;


